import React, { useState, useEffect } from 'react';
import { User, KeyRound, Mail, Phone, Save, Loader2, Check, X, Receipt } from 'lucide-react';
import { translations } from '../../translations';
import { supabase } from '../../lib/supabase';
import { InvoiceHistory } from '../../components/Plans/InvoiceHistory';

interface AccountProps {
  isDarkMode: boolean;
  language: string;
}

export function Account({ isDarkMode, language }: AccountProps) {
  const [profile, setProfile] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  
  // Additional state variables for proper error handling
  const [profileError, setProfileError] = useState<string | null>(null);
  const [profileSuccess, setProfileSuccess] = useState<string | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  
  const t = translations[language as keyof typeof translations];

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        setEmail(user.email || '');
        
        const { data: profileData, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          setProfileError('Failed to load profile');
        } else if (profileData) {
          setProfile(profileData);
          setFirstName(profileData.first_name || '');
          setLastName(profileData.last_name || '');
          setPhone(profileData.phone || '');
        }
      }
    } catch (error) {
      setProfileError('Failed to load profile');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateProfile = async () => {
    try {
      setIsProfileLoading(true);
      setProfileError(null);
      setProfileSuccess(null);

      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setProfileError('User not authenticated');
        return;
      }

      const { error } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          first_name: firstName,
          last_name: lastName,
          phone: phone,
          updated_at: new Date().toISOString()
        });

      if (error) {
        setProfileError('Failed to update profile');
      } else {
        setProfileSuccess('Profile updated successfully');
        setTimeout(() => setProfileSuccess(null), 3000);
      }
    } catch (error) {
      setProfileError('Failed to update profile');
    } finally {
      setIsProfileLoading(false);
    }
  };

  const handleChangePassword = async () => {
    try {
      setIsPasswordLoading(true);
      setPasswordError(null);
      setPasswordSuccess(null);

      if (newPassword !== confirmPassword) {
        setPasswordError('Passwords do not match');
        return;
      }

      if (newPassword.length < 6) {
        setPasswordError('Password must be at least 6 characters');
        return;
      }

      const { error } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (error) {
        setPasswordError(error.message);
      } else {
        setPasswordSuccess('Password updated successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => setPasswordSuccess(null), 3000);
      }
    } catch (error) {
      setPasswordError('Failed to update password');
    } finally {
      setIsPasswordLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Loader2 size={32} className="animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col">
      <div className="p-8 flex-1 overflow-y-auto">
        <h1 className={`text-2xl font-semibold mb-6 ${
          isDarkMode ? 'text-gray-200' : 'text-gray-900'
        }`}>
          {t.my_account}
        </h1>

        <div className="space-y-6">
          {/* Personal Information */}
          <div className={`p-6 rounded-lg border ${
            isDarkMode 
              ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
              : 'bg-white border-[#e5e7eb]'
          }`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-2 rounded-lg ${
                isDarkMode ? 'bg-[#2b2b2b]' : 'bg-gray-100'
              }`}>
                <User size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
              </div>
              <h2 className={`text-lg font-semibold ${
                isDarkMode ? 'text-gray-200' : 'text-gray-900'
              }`}>{t.personal_information}</h2>
            </div>

            {profileError && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-500 text-sm">
                {profileError}
              </div>
            )}

            {profileSuccess && (
              <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-500 text-sm flex items-center gap-2">
                <Check size={16} />
                {profileSuccess}
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium mb-1.5 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t.first_name}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm ${
                        isDarkMode
                          ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                          : 'bg-gray-50 text-gray-900 border-gray-200'
                      } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                      placeholder={t.first_name_placeholder}
                    />
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>

                <div>
                  <label className={`block text-sm font-medium mb-1.5 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    {t.last_name}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm ${
                        isDarkMode
                          ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                          : 'bg-gray-50 text-gray-900 border-gray-200'
                      } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                      placeholder={t.last_name_placeholder}
                    />
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  </div>
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1.5 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {t.email}
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    readOnly
                    className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm bg-opacity-50 cursor-not-allowed ${
                      isDarkMode 
                        ? 'bg-[#2b2b2b] text-gray-400 border-[#2b2b2b]' 
                        : 'bg-gray-50 text-gray-500 border-gray-200'
                    }`}
                  />
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1.5 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {t.phone}
                </label>
                <div className="relative">
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm ${
                      isDarkMode
                        ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                        : 'bg-gray-50 text-gray-900 border-gray-200'
                    } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                    placeholder={t.phone_placeholder}
                  />
                  <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleUpdateProfile}
                  disabled={isProfileLoading}
                  className="bg-orange-500 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-orange-600 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isProfileLoading ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      {t.please_wait}
                    </>
                  ) : (
                    <>
                      <Save size={14} strokeWidth={2.3} />
                      {t.save}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Change Password */}
          <div className={`p-6 rounded-lg border ${
            isDarkMode 
              ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
              : 'bg-white border-[#e5e7eb]'
          }`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-2 rounded-lg ${
                isDarkMode ? 'bg-[#2b2b2b]' : 'bg-gray-100'
              }`}>
                <KeyRound size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
              </div>
              <h2 className={`text-lg font-semibold ${
                isDarkMode ? 'text-gray-200' : 'text-gray-900'
              }`}>{t.change_password}</h2>
            </div>

            {passwordError && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 text-red-500 text-sm">
                {passwordError}
              </div>
            )}

            {passwordSuccess && (
              <div className="mb-4 p-3 rounded-lg bg-green-50 text-green-500 text-sm flex items-center gap-2">
                <Check size={16} />
                {passwordSuccess}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {t.current_password}
                </label>
                <div className="relative">
                  <input
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm ${
                      isDarkMode
                        ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                        : 'bg-gray-50 text-gray-900 border-gray-200'
                    } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                    placeholder={t.current_password_placeholder}
                  />
                  <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1.5 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {t.new_password}
                </label>
                <div className="relative">
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm ${
                      isDarkMode
                        ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                        : 'bg-gray-50 text-gray-900 border-gray-200'
                    } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                    placeholder={t.new_password_placeholder}
                  />
                  <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1.5 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-700'
                }`}>
                  {t.confirm_password}
                </label>
                <div className="relative">
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`w-full rounded-lg pl-10 pr-4 py-2.5 text-sm ${
                      isDarkMode
                        ? 'bg-[#2b2b2b] text-gray-200 border-[#2b2b2b]'
                        : 'bg-gray-50 text-gray-900 border-gray-200'
                    } focus:outline-none focus:ring-2 focus:ring-orange-500`}
                    placeholder={t.confirm_password_placeholder}
                  />
                  <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleChangePassword}
                  disabled={isPasswordLoading}
                  className="bg-orange-500 text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-orange-600 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPasswordLoading ? (
                    <>
                      <Loader2 size={14} className="animate-spin" />
                      {t.please_wait}
                    </>
                  ) : (
                    <>
                      <Save size={14} strokeWidth={2.3} />
                      {t.update}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Invoice History */}
          <div className={`p-6 rounded-lg border ${
            isDarkMode 
              ? 'bg-[#1c1c1c] border-[#2b2b2b]' 
              : 'bg-white border-[#e5e7eb]'
          }`}>
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-2 rounded-lg ${
                isDarkMode ? 'bg-[#2b2b2b]' : 'bg-gray-100'
              }`}>
                <Receipt size={20} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
              </div>
              <h2 className={`text-lg font-semibold ${
                isDarkMode ? 'text-gray-200' : 'text-gray-900'
              }`}>{t.invoice_history}</h2>
            </div>
            
            <InvoiceHistory isDarkMode={isDarkMode} language={language} />
          </div>
        </div>
      </div>
    </div>
  );
}